import type { AxiosError } from 'axios'
import { flattenObject } from '@/utils'
import { Api } from '@apiTypes'
import { clearAuthDataAndRedirect, hasRefreshToken, refreshTokenHelper, setHeaders } from '@/auth/utils'

// Per-request switches read by setHeaders and the interceptors below: send no Authorization header,
// do not refresh the tokens on a 401, do not sign out on a 401 that is not refreshed.
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- axios's type parameters, which a merged declaration repeats
  interface AxiosRequestConfig<D, P> {
    skipAuthToken?: boolean
    skipAuthRefresh?: boolean
    skipLogout?: boolean
  }
}

const api = new Api()
const apiAxiosInstance = api.instance

if (import.meta.env.MODE === 'development' && (!window.location.port || window.location.port !== '80')) {
  // This is fix for development env, if you want to access via FE port (currently 5173) directly
  const newBaseUrl = `http://${window.location.hostname}:8010/`

  console.log('[AXIOS] Changing API base URL', newBaseUrl)
  apiAxiosInstance.defaults.baseURL = newBaseUrl
}

apiAxiosInstance.interceptors.request.use(config => {
  if (import.meta.env.MODE === 'development')
    console.log('[AXIOS] Request Interceptors', config)

  setHeaders(config)

  return config
}, (error: any) => {
  console.error('[AXIOS] Request Interceptors Error', error)

  return Promise.reject(error)
})

apiAxiosInstance.interceptors.response.use(response => {
  if (import.meta.env.VITE_AXIOS_DEBUG === 'true')
    console.log('[AXIOS] Response Interceptors', response)

  return response
}, async (err: AxiosError) => {
  console.error('[AXIOS] Response Interceptors Error', err)
  switch (err.response?.status) {
    case 400:
      err.response.data = flattenObject(err.response.data)
      break
    case 401: {
      const config = err.config!

      if (hasRefreshToken() && !config.skipAuthRefresh) {
        // Refreshes, or takes the tokens another tab has refreshed since then.
        return refreshTokenHelper(config).then(() => {
          setHeaders(config)
          config.skipAuthRefresh = true

          return apiAxiosInstance.request(config)
        }, refreshTokenError => {
          // Refused (and no other tab has replaced it), or signed out in another
          // tab meanwhile: the session is over.
          if (refreshTokenError.response?.status === 401 || !hasRefreshToken()) {
            clearAuthDataAndRedirect()

            throw err
          }

          throw refreshTokenError
        })
      }
      else if (!config.skipLogout) {
        clearAuthDataAndRedirect()
      }
    }
  }

  return Promise.reject(err)
})

apiAxiosInstance.defaults.xsrfHeaderName = 'X-CSRFToken'
apiAxiosInstance.defaults.xsrfCookieName = 'csrftoken'
apiAxiosInstance.defaults.withCredentials = true

export const axiosInstance = apiAxiosInstance
export default api
